var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * [CLAIMS_CONTEXT]
 * purpose: Manages swimming pool cover external damage declaration by allowing
 * users to select damaged panel materials and configure the quantity of panels
 * for each selected material type.
 * touches: external damage declaration flow, swimming pool cover damage forms,
 * panel material selection and panel quantity management.
 * invariants: panel data remains synchronized with the externalDamage store;
 * selected materials always have at least one associated panel; removing a
 * material removes its related panels; panel material values reuse the veranda
 * panel material model for API compatibility.
 * inputs: externalDamage entity inherited from ExternalDamageComponent,
 * selected panel materials, panel counts, panel material enums and changeKey
 * identifying the edited damage entity.
 * outputs: updates externalDamage panels collection through updateExternalDamage;
 * renders material selection and panel quantity configuration components.
 * see: frontend/src/declaration/components/externalDamage/ExternalDamageComponent,
 * frontend/src/declaration/components/externalDamage/DamageDescription/Veranda/Veranda.vue,
 * frontend/src/declaration/components/externalDamage/DamageDescription/SwimpoolCover/SwimpoolCoverPanels.vue,
 * frontend/src/declaration/components/externalDamage/DamageDescription/SwimpoolCover/SwimpoolCoverPanelMaterials.vue,
 * frontend/src/shared/types/api/externalDamage.ts
 */
import Component from 'vue-class-component';
import SwimpoolCoverPanels from './SwimpoolCover/SwimpoolCoverPanels.vue';
import SwimpoolCoverPanelMaterials from './SwimpoolCover/SwimpoolCoverPanelMaterials.vue';
import { ExternalDamageComponent } from '@declaration/components/externalDamage/ExternalDamageComponent';
import { VerandaPanelGlazingType, VerandaPanelMaterial, } from '@shared/types/api/externalDamage';
let SwimpoolCover = class SwimpoolCover extends ExternalDamageComponent {
    get panels() {
        var _a;
        return ((_a = this.externalDamage.panels) !== null && _a !== void 0 ? _a : []);
    }
    get selectedMaterials() {
        return Object.values(VerandaPanelMaterial).filter((material) => this.panels.some((panel) => (panel === null || panel === void 0 ? void 0 : panel.material) === material));
    }
    set selectedMaterials(materials) {
        if (!materials.includes(VerandaPanelMaterial.Glass)) {
            this.glassPanelCount = 0;
        }
        else {
            this.glassPanelCount || (this.glassPanelCount = 1);
        }
        if (!materials.includes(VerandaPanelMaterial.Polycarbonate)) {
            this.polycarbonatePanelCount = 0;
        }
        else {
            this.polycarbonatePanelCount || (this.polycarbonatePanelCount = 1);
        }
        if (!materials.includes(VerandaPanelMaterial.SandwichPanel)) {
            this.sandwichPanelCount = 0;
        }
        else {
            this.sandwichPanelCount || (this.sandwichPanelCount = 1);
        }
        if (!materials.includes(VerandaPanelMaterial.Other)) {
            this.otherPanelCount = 0;
        }
        else {
            this.otherPanelCount || (this.otherPanelCount = 1);
        }
    }
    get glassPanels() {
        return this.panels.filter((panel) => (panel === null || panel === void 0 ? void 0 : panel.material) === VerandaPanelMaterial.Glass);
    }
    get polycarbonatePanels() {
        return this.panels.filter((panel) => (panel === null || panel === void 0 ? void 0 : panel.material) === VerandaPanelMaterial.Polycarbonate);
    }
    get sandwichPanels() {
        return this.panels.filter((panel) => (panel === null || panel === void 0 ? void 0 : panel.material) === VerandaPanelMaterial.SandwichPanel);
    }
    get otherPanels() {
        return this.panels.filter((panel) => (panel === null || panel === void 0 ? void 0 : panel.material) === VerandaPanelMaterial.Other);
    }
    get glassPanelCount() {
        return this.glassPanels.length;
    }
    set glassPanelCount(count) {
        var _a, _b;
        const diff = count - this.glassPanelCount;
        if (diff === 0)
            return;
        const panels = this.panels.slice();
        const materialPanels = this.glassPanels.slice();
        for (let i = 0; i < Math.abs(diff); i++) {
            if (diff > 0) {
                panels.push({
                    material: VerandaPanelMaterial.Glass,
                    glazingType: (_b = (_a = this.glassPanels.find((panel) => panel === null || panel === void 0 ? void 0 : panel.glazingType)) === null || _a === void 0 ? void 0 : _a.glazingType) !== null && _b !== void 0 ? _b : VerandaPanelGlazingType.Unknown,
                });
            }
            else {
                const panel = materialPanels.pop();
                const realIndex = panels.indexOf(panel);
                if (realIndex !== -1)
                    panels.splice(realIndex, 1);
            }
        }
        this.updateExternalDamage({ panels });
    }
    get polycarbonatePanelCount() {
        return this.polycarbonatePanels.length;
    }
    set polycarbonatePanelCount(count) {
        this.updateMaterialPanelsCount(VerandaPanelMaterial.Polycarbonate, count, this.polycarbonatePanels);
    }
    get sandwichPanelCount() {
        return this.sandwichPanels.length;
    }
    set sandwichPanelCount(count) {
        this.updateMaterialPanelsCount(VerandaPanelMaterial.SandwichPanel, count, this.sandwichPanels);
    }
    get otherPanelCount() {
        return this.otherPanels.length;
    }
    set otherPanelCount(count) {
        this.updateMaterialPanelsCount(VerandaPanelMaterial.Other, count, this.otherPanels);
    }
    updateMaterialPanelsCount(material, count, currentPanels) {
        const diff = count - currentPanels.length;
        if (diff === 0)
            return;
        const panels = this.panels.slice();
        const materialPanels = currentPanels.slice();
        for (let i = 0; i < Math.abs(diff); i++) {
            if (diff > 0) {
                panels.push({ material, glazingType: null });
            }
            else {
                const panel = materialPanels.pop();
                const realIndex = panels.indexOf(panel);
                if (realIndex !== -1)
                    panels.splice(realIndex, 1);
            }
        }
        this.updateExternalDamage({ panels });
    }
    getPanelsCount(material) {
        switch (material) {
            case VerandaPanelMaterial.Glass:
                return this.glassPanelCount;
            case VerandaPanelMaterial.Polycarbonate:
                return this.polycarbonatePanelCount;
            case VerandaPanelMaterial.SandwichPanel:
                return this.sandwichPanelCount;
            case VerandaPanelMaterial.Other:
                return this.otherPanelCount;
        }
    }
    setPanelsCount(material, count) {
        switch (material) {
            case VerandaPanelMaterial.Glass:
                this.glassPanelCount = count;
                break;
            case VerandaPanelMaterial.Polycarbonate:
                this.polycarbonatePanelCount = count;
                break;
            case VerandaPanelMaterial.SandwichPanel:
                this.sandwichPanelCount = count;
                break;
            case VerandaPanelMaterial.Other:
                this.otherPanelCount = count;
                break;
        }
    }
};
SwimpoolCover = __decorate([
    Component({
        components: {
            SwimpoolCoverPanelMaterials,
            SwimpoolCoverPanels,
        },
    })
], SwimpoolCover);
export default SwimpoolCover;
